import type {
  FinalizeJardinierReportResponse,
  JardinierReportFinalizePayload,
} from "@mytour/shared";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {jardinierReportApiService} from "./jardinierReportApi.service";

export const jardinierReportKeysStore = {
  root: ["jardinierReport"] as const,
  report: (eventId: string) => [...jardinierReportKeysStore.root, eventId] as const,
  documentsByCustomer: (customerId: string) =>
    [...jardinierReportKeysStore.root, "customer", customerId, "documents"] as const,
};

export function useJardinierReport(eventId: string, enabled = true) {
  const {data, isLoading} = useQuery({
    queryKey: jardinierReportKeysStore.report(eventId),
    queryFn: () => jardinierReportApiService.getReport(eventId),
    enabled: enabled && !!eventId,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  return {
    jardinierReport: data,
    isJardinierReportLoading: isLoading,
  };
}

export function useJardinierReportDocumentsByCustomer(
  customerId: string | undefined,
  enabled = true
) {
  const {data, isLoading} = useQuery({
    queryKey: jardinierReportKeysStore.documentsByCustomer(customerId as string),
    queryFn: () => jardinierReportApiService.listDocumentsByCustomer(customerId as string),
    enabled: enabled && !!customerId,
    refetchOnWindowFocus: false,
    // Aligné sur `useCertificatRamonageDocumentsByCustomer` — la timeline Documents mêle les sources.
    staleTime: 2 * 60 * 1000,
  });

  return {
    jardinierReportDocuments: data,
    isJardinierReportDocumentsLoading: isLoading,
  };
}

export interface FinalizeJardinierReportVariables {
  eventId: string;
  payload: JardinierReportFinalizePayload;
}

export function useFinalizeJardinierReport() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: (vars: FinalizeJardinierReportVariables) =>
      jardinierReportApiService.finalizeReport(vars.eventId, vars.payload),
    onSuccess(result: FinalizeJardinierReportResponse, vars) {
      queryClient.setQueryData(jardinierReportKeysStore.report(vars.eventId), result.report);
      queryClient.invalidateQueries({
        queryKey: jardinierReportKeysStore.root,
      });
    },
    retry: false,
  });

  return {finalizeJardinierReport: mutateAsync, isFinalizingJardinierReport: isPending};
}

export function useResendJardinierReport() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: (eventId: string) => jardinierReportApiService.resend(eventId),
    onSuccess() {
      /**
       * Statut d'envoi lu à la fois par la fiche événement et par Documents — invalider
       * toute la racine plutôt que de traquer les deux clés individuellement.
       */
      queryClient.invalidateQueries({queryKey: jardinierReportKeysStore.root});
    },
  });

  return {resendJardinierReport: mutateAsync, isResendingJardinierReport: isPending};
}
